import React, { useEffect } from 'react'
import { AppState, AppStateStatus } from 'react-native'
import { useVideoPlayer } from 'expo-video'

import { StyledViewVideoContainer, StyledLinearGradient, StyledVideo } from './SlideVideo.styles'
import {
  AppStateWithRemoveEventListener,
  SlideVideoProps,
  VideoPlayerInstance,
  WebVideoListeners,
} from './SlideVideo.types'
import { isWeb } from 'APP/Helpers/checkPlatform'

const WEB_VIDEO_INITIAL_TIMEOUT_MS = 500

const resumeWebVideo = (videoPlayer: VideoPlayerInstance) => {
  try {
    videoPlayer.play()
    videoPlayer.loop = true
    videoPlayer.muted = true
  } catch {}
}

const createWebInitialTimeout = (videoPlayer: VideoPlayerInstance) =>
  setTimeout(() => {
    resumeWebVideo(videoPlayer)
  }, WEB_VIDEO_INITIAL_TIMEOUT_MS)

const createWebVideoListeners = (videoPlayer: VideoPlayerInstance): WebVideoListeners => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      resumeWebVideo(videoPlayer)
    }
  }

  const handleWindowFocus = () => {
    resumeWebVideo(videoPlayer)
  }

  return { handleVisibilityChange, handleWindowFocus }
}

const attachWebVideoListeners = ({
  handleVisibilityChange,
  handleWindowFocus,
}: WebVideoListeners) => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleWindowFocus)
}

const detachWebVideoListeners = ({
  handleVisibilityChange,
  handleWindowFocus,
}: WebVideoListeners) => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('focus', handleWindowFocus)
}

export const SlideVideo: React.FC<SlideVideoProps> = ({ color, video }) => {
  const videoPlayer = useVideoPlayer(video, (player) => {
    player.audioMixingMode = 'mixWithOthers'
    player.loop = true
    player.muted = true
    player.play()
  })

  useEffect(() => {
    if (!isWeb()) return

    const initialTimeout = createWebInitialTimeout(videoPlayer)

    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return () => {
        clearTimeout(initialTimeout)
      }
    }

    const listeners = createWebVideoListeners(videoPlayer)

    attachWebVideoListeners(listeners)

    return () => {
      clearTimeout(initialTimeout)
      detachWebVideoListeners(listeners)
    }
  }, [videoPlayer])

  useEffect(() => {
    if (isWeb()) return

    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (nextState === 'active') {
        try {
          videoPlayer.play()
          videoPlayer.loop = true
          videoPlayer.muted = true
        } catch {}
      }
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      if ('remove' in subscription && typeof subscription.remove === 'function') {
        subscription.remove()
        return
      }

      ;(AppState as AppStateWithRemoveEventListener).removeEventListener?.(
        'change',
        handleAppStateChange
      )
    }
  }, [videoPlayer])

  return (
    <StyledViewVideoContainer testID="SLIDE_VIDEO_CONTAINER">
      <StyledVideo
        player={videoPlayer}
        nativeControls={false}
        contentFit="cover"
        allowsPictureInPicture={false}
        startsPictureInPictureAutomatically={false}
      />
      <StyledLinearGradient colors={['transparent', color]} locations={[0.1, 0.85]} />
    </StyledViewVideoContainer>
  )
}
